// Public contact page. Posts to /api/contact and renders a soft success card
// with the returned ticket id. Layout: brand panel on the left (40%), form
// on the right (60%); stacked on mobile.

import { useState } from "react";
import { Link } from "react-router";
import {
  BloomLogo,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@bloom/ui";
import { Clock3, Mail, Sparkles } from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";
import { api } from "../api/client.ts";

// Optional categories so a contact submission can be triaged at the inbox
// without forcing the user to pick one. Keys mirror /api/support's required
// enum so future merging is one-line.
const CONTACT_CATEGORIES = ["playback", "subtitles", "payments", "account", "other"] as const;
type ContactCategory = (typeof CONTACT_CATEGORIES)[number];

// TODO(client-asset): swap to the client-confirmed support inbox alias.
const SUPPORT_EMAIL_DISPLAY = "support@bloom.tv";

export function ContactUsPage() {
  return (
    <ContactShell
      kind="contact"
      eyebrowKey="contact.eyebrow"
      titleKey="contact.title"
      subtitleKey="contact.subtitle"
      panelTaglineKey="contact.panel.tagline"
      replyWindowKey="contact.panel.reply_window"
      submitEndpoint="/api/contact"
      categoryRequired={false}
    />
  );
}

interface ContactShellProps {
  kind: "contact" | "support";
  eyebrowKey: string;
  titleKey: string;
  subtitleKey: string;
  panelTaglineKey: string;
  replyWindowKey: string;
  submitEndpoint: string;
  categoryRequired: boolean;
}

// Exported so /report-a-problem can reuse the exact same shell with one
// flipped flag (categoryRequired) and a different endpoint.
export function ContactShell(props: ContactShellProps) {
  const { t, locale } = useLocale();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<ContactCategory | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<{ id: string; at: string } | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ ticketId: string; queuedAt: string }>(props.submitEndpoint, {
        name,
        email,
        subject,
        message,
        // Always send when set — the server validates required-ness per route.
        ...(category ? { category } : {}),
        locale,
      });
      setTicket({ id: res.ticketId, at: res.queuedAt });
      // Clear the form fields so the success card stands alone and a
      // returning visitor doesn't see their previous message ghosted in.
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setCategory("");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-4rem)] bg-[var(--brand-paper)] lg:grid-cols-[2fr_3fr]">
      {/* LEFT — brand panel. Hidden under lg so the form gets full width on
          mobile; the headline + reply window still appear above the form. */}
      <aside
        className="relative isolate hidden overflow-hidden bg-[var(--brand-ink)] p-12 lg:flex lg:flex-col lg:justify-between"
        style={{
          backgroundImage: `url(/auth-imagery/${props.kind === "support" ? "report" : "contact"}.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Ink-from-bottom gradient + soft rose tint on top — keeps the photo
            present but pushes enough contrast under the text. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background:radial-gradient(40rem_28rem_at_-10%_-10%,rgba(233,75,122,0.35),transparent_55%)]"
        />
        <BloomLogo className="relative h-9 text-white" />
        <div className="relative">
          <p className="font-display text-3xl leading-tight text-white drop-shadow-md">
            {t(props.panelTaglineKey)}
          </p>
          <div className="mt-8 space-y-3 text-sm text-white/85">
            <p className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[var(--brand-pink)]" aria-hidden />
              {t(props.replyWindowKey)}
            </p>
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[var(--brand-pink)]" aria-hidden />
              <span>
                {t("contact.panel.or_email")}{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL_DISPLAY}`}
                  className="font-semibold text-white underline decoration-[var(--brand-rose)] decoration-2 underline-offset-4 hover:text-[var(--brand-pink)]"
                >
                  {SUPPORT_EMAIL_DISPLAY}
                </a>
              </span>
            </p>
          </div>
        </div>
      </aside>

      {/* RIGHT — form column. */}
      <section className="flex flex-col justify-center px-6 py-12 sm:px-10 sm:py-16">
        <div className="mx-auto w-full max-w-xl space-y-6">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-pink-soft)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--brand-rose)]">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              {t(props.eyebrowKey)}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {t(props.titleKey)}
            </h1>
            <p className="mt-2 text-base text-[var(--brand-ink)]/75">{t(props.subtitleKey)}</p>
          </div>

          {ticket ? (
            <SuccessCard ticketId={ticket.id} onNew={() => setTicket(null)} kind={props.kind} />
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("contact.form.name")} htmlFor="name">
                  <Input
                    id="name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Field>
                <Field label={t("contact.form.email")} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Field>
              </div>

              <Field label={t("contact.form.subject")} htmlFor="subject">
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </Field>

              <Field
                label={
                  props.categoryRequired
                    ? t("support.form.category_required")
                    : t("contact.form.category")
                }
                htmlFor="category"
              >
                <Select
                  value={category}
                  onValueChange={(v) => setCategory(v as ContactCategory)}
                  required={props.categoryRequired}
                >
                  <SelectTrigger id="category" aria-label={t("contact.form.category")}>
                    <SelectValue placeholder={t("contact.form.category_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {t(`support.category.${c}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t("contact.form.message")} htmlFor="message">
                <textarea
                  id="message"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  className={cn(
                    "block w-full rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-paper)] px-4 py-3 text-sm",
                    "placeholder:text-[var(--brand-muted)]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-rose",
                  )}
                />
              </Field>

              {error ? (
                <p className="text-sm text-[var(--brand-rose)]" role="alert">
                  {error}
                </p>
              ) : null}

              <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                {submitting
                  ? t("contact.form.sending")
                  : props.kind === "support"
                    ? t("support.form.submit")
                    : t("contact.form.submit")}
              </Button>

              <p className="text-xs text-[var(--brand-muted)]">
                {t("about.contact.cta_faq")} →{" "}
                <Link to="/faq" className="font-semibold text-[var(--brand-rose)] hover:underline">
                  {t("footer.faq")}
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-sm font-semibold text-[var(--brand-ink)]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SuccessCard({
  ticketId,
  onNew,
  kind,
}: {
  ticketId: string;
  onNew: () => void;
  kind: "contact" | "support";
}) {
  const { t } = useLocale();
  return (
    <div className="rounded-3xl border border-[var(--brand-pink)] bg-[var(--brand-pink-soft)] p-6 sm:p-8">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-rose)] text-white">
        <Sparkles className="h-6 w-6" aria-hidden />
      </div>
      <h2 className="mt-4 font-heading text-2xl font-bold text-[var(--brand-ink)]">
        {t("contact.success.title")}
      </h2>
      <p className="mt-2 text-sm text-[var(--brand-ink)]/75">{t("contact.success.body")}</p>
      <div className="mt-6 rounded-2xl border border-[var(--brand-border)] bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-muted)]">
          {t("contact.success.ticket")}
        </p>
        <p className="mt-1 font-mono text-lg font-bold tracking-tight text-[var(--brand-ink)]">
          {ticketId}
        </p>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button variant="rose" onClick={onNew}>
          {t("contact.success.new_message")}
        </Button>
        <Link
          to={kind === "support" ? "/" : "/faq"}
          className="inline-flex h-11 items-center justify-center rounded-full border border-[var(--brand-border)] bg-white px-5 text-sm font-semibold text-[var(--brand-ink)] hover:bg-[var(--brand-pink-soft)]"
        >
          {kind === "support" ? t("nav.home") : t("footer.faq")}
        </Link>
      </div>
    </div>
  );
}
